import {
    FormGroup,
    InputAdornment,
    InputLabel,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    TypographyVariant,
} from '@mui/material'
import { forwardRef } from 'react'
import { textfieldStyles } from './styles'

export type TypographyVariantProps = {
    typographyVariant?: TypographyVariant
}
export type HandlerProps = {
    onReturn?: () => void
    onCancel?: () => void
}
export type TextFieldProps = MuiTextFieldProps & TypographyVariantProps & HandlerProps

const KEY_ENTER = 13
const KEY_ESCAPE = 27

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
    ({ typographyVariant, onReturn, onCancel, type, ...props }: TextFieldProps, inputRef) => {
        const onKeyDown = (e): void => {
            if (e.keyCode === KEY_ENTER && onReturn !== undefined) {
                e.preventDefault()
                onReturn()
            }
            if (e.keyCode === KEY_ESCAPE && onCancel !== undefined) {
                e.preventDefault()
                onCancel()
            }
        }

        // spread operations defined in this order to:
        // 1 - define variant outlined unless otherwise defined
        // 2 - take all props
        // 3 - override label to empty because we are defining label with FormLabel
        const finalProps = {
            variant: 'outlined' as const,
            ...props,
            label: '',
        }

        // https://bugs.chromium.org/p/chromium/issues/detail?id=468153
        // Setting something that's semantic and not off for autocomplete fields removes autofill
        if (type === 'password') {
            finalProps.autoComplete = 'new-password'
        }

        const id = props.id

        return (
            <FormGroup>
                {props.label && (
                    <InputLabel sx={textfieldStyles.fieldLabel} htmlFor={id}>
                        {props.label}
                    </InputLabel>
                )}
                <MuiTextField
                    size="small"
                    InputProps={{
                        startAdornment: props.prefix && (
                            <InputAdornment position="start">{props.prefix}</InputAdornment>
                        ),
                        ...props.InputProps,
                    }}
                    id={id}
                    onKeyDown={onKeyDown}
                    inputRef={inputRef}
                    sx={typographyVariant ? textfieldStyles[typographyVariant] : textfieldStyles.body1}
                    type={type}
                    {...finalProps}
                />
            </FormGroup>
        )
    },
)
